import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Edit, Activity, Plus, Server } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import APITable from "../../../components/Tables/APITable";
import Pagination from "../../../components/Pagination/Pagination";
import Button from "../../../components/Button/Button";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import Notification from "../../../components/Notification/Notification";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../../apiService";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./AdminCatalogue.module.scss";

const AdminCatalogue = () => {
  const navigate = useNavigate();

  /* ==================================================
     STATE
  ================================================== */
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");

  // Pagination meta
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Confirm Modals
  const [statusModal, setStatusModal] = useState(null);
  const [failoverModal, setFailoverModal] = useState(null);
  const [downModal, setDownModal] = useState(null);

  // Notifications
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* ==================================================
     TABLE COLUMNS
  ================================================== */
  const columns = [
    { label: "Code", key: "code" },
    { label: "API Name", key: "apiName" },
    { label: "Category", key: "category_name" },
    { label: "Vendor", key: "vendor" },
    { label: "Cost (Rs)", key: "cost" },
    { label: "Credit", key: "credit" },
    { label: "Failover Api", key: "failover_api" },
    { label: "Level", key: "level" },
    { label: "Is Running", key: "api_call" },
    { label: "Failover", key: "failover_status" },
    { label: "Publish", key: "status" },
    { label: "Is Down", key: "down" },
    { label: "Actions", key: "actions" },
  ];

  /* ==================================================
     FETCH API LIST
  ================================================== */
  const fetchApis = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet(
        "catalog",
        `/apimaster/list?page=${currentPage}&limit=${rowsPerPage}&search=${searchText}`,
      );

      if (response?.success) {
        const { data, pagination } = response;

        setApis(
          data.map((api) => ({
            id: api.id,
            code: api.code ?? "--",
            level: api.level ?? "--",
            vendor: api.vendor_name ?? "--",
            category_name: api.category_name ?? "--",
            apiName: api.name ?? "--",
            cost: api.add_cost ? `${api.add_cost}` : "--",
            credit: api.add_credits ?? "--",
            api_call: api.api_call ?? "--",
            failover_api: api.failover_api ?? "--",
            failoverActive: api.api_call === "Running",
            active: api.status === "Publish",
            failoverApiId: api.failover_api_id,
            hasFailover: !!api.failover_api,
            downactive: api.is_down === true,
          })),
        );

        setPagination(pagination);
      }
    } catch (error) {
      console.error("Failed to fetch API list", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchText]);

  useEffect(() => {
    fetchApis();
  }, [fetchApis]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  /* ==================================================
     STATUS TOGGLE HANDLERS
  ================================================== */
  const handleDownToggle = (row) => setDownModal(row);
  const handleToggle = (row) => setStatusModal(row);
  const handleFailoverToggle = (row) => setFailoverModal(row);

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchText(searchInput);
  };

  const confirmDownToggle = async () => {
    if (!downModal) return;
    const nextValue = !downModal.downactive;
    try {
      await apiPost("catalog", "/apimaster/status/downandstore", {
        id: downModal.id,
        isDown: nextValue,
      });
      showNotification(
        `API ${nextValue ? "marked as DOWN" : "RESTORED successfully"}`,
        "success",
      );
      fetchApis();
    } catch (err) {
      showNotification(
        err?.response?.data?.message || "Down status update failed",
        "error",
      );
    } finally {
      setDownModal(null);
    }
  };

  const confirmFailoverToggle = async () => {
    if (!failoverModal) return;
    const nextApiCall =
      failoverModal.api_call === "Running" ? "Stop" : "Running";
    try {
      await apiPost("catalog", "/apimaster/filover/update", {
        id: failoverModal.id,
        apiCall: nextApiCall,
        filoverApiId: failoverModal.failoverApiId,
      });
      showNotification(
        `Failover ${nextApiCall === "Stop" ? "disabled" : "enabled"} successfully`,
        "success",
      );
      fetchApis();
    } catch (err) {
      showNotification(
        err?.response?.data?.message || "Failover update failed",
        "error",
      );
    } finally {
      setFailoverModal(null);
    }
  };

  const confirmToggle = async () => {
    if (!statusModal) return;
    const newStatus = statusModal.active ? "Unpublish" : "Publish";

    // Optimistic Update
    setApis((prev) =>
      prev.map((api) =>
        api.id === statusModal.id ? { ...api, active: !api.active } : api,
      ),
    );

    try {
      await apiPost("catalog", "/apimaster/status/update", {
        id: statusModal.id,
        status: newStatus,
      });
      showNotification(
        `API ${newStatus === "Publish" ? "published" : "unpublished"} successfully`,
        "success",
      );
    } catch (error) {
      // Rollback on failure
      setApis((prev) =>
        prev.map((api) =>
          api.id === statusModal.id ? { ...api, active: !api.active } : api,
        ),
      );
      showNotification(
        error?.response?.data?.message || "Status update failed",
        "error",
      );
    } finally {
      setStatusModal(null);
    }
  };

  /* ==================================================
     TABLE CELL RENDER
  ================================================== */
  const renderCell = (row, key) => {
    if (key === "api_call") {
      const value = row.api_call;
      let badgeClass = styles["admin-catalogue__badge--default"];

      if (value === "Running")
        badgeClass = styles["admin-catalogue__badge--running"];
      if (value === "Stop") badgeClass = styles["admin-catalogue__badge--stop"];

      return (
        <div className={`${styles["admin-catalogue__badge"]} ${badgeClass}`}>
          {value}
        </div>
      );
    }

    if (key === "failover_status") {
      if (!row.hasFailover) return <span>--</span>;
      return (
        <div
          title={row.failoverActive ? "Disable failover" : "Enable failover"}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <ToggleSwitch
            isOn={row.failoverActive}
            onToggle={() => handleFailoverToggle(row)}
          />
        </div>
      );
    }

    if (key === "status") {
      if (!can("api-catalogue", "publish-api")) return <span>—</span>;
      return (
        <div
          title={row.active ? "Unpublish" : "Publish"}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <ToggleSwitch isOn={row.active} onToggle={() => handleToggle(row)} />
        </div>
      );
    }

    if (key === "down") {
      if (!can("api-catalogue", "down-api")) return <span>—</span>;
      return (
        <div
          title={row.downactive ? "Restore API" : "Mark API as down"}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <ToggleSwitch
            isOn={row.downactive}
            onToggle={() => handleDownToggle(row)}
          />
        </div>
      );
    }

    if (key === "actions") {
      return (
        <div className={styles["admin-catalogue__actions-cell"]}>
          <div
            className={styles["admin-catalogue__action-icon"]}
            title="View API Details"
            onClick={() => navigate(`/admin/admin-catalogue/${row.id}`)}
          >
            <Eye size={18} strokeWidth={2.5} />
          </div>

          {can("api-catalogue", "update") && (
            <div
              className={styles["admin-catalogue__action-icon"]}
              title="Edit API"
              onClick={() => navigate(`/admin/admin-catalogue/edit/${row.id}`)}
            >
              <Edit size={18} strokeWidth={2.5} />
            </div>
          )}

          {can("api-catalogue", "try-out") && (
            <Button
              text="Try Out"
              onClick={() =>
                navigate(`/admin/admin-catalogue/${row.id}/tryout`)
              }
              style={{ padding: "8px 16px" }}
            />
          )}
        </div>
      );
    }

    return row[key];
  };

  /* ==================================================
     JSX
  ================================================== */
  return (
    <div className={styles["admin-catalogue__page"]}>
      {/* NOTIFICATIONS */}
      {notification && (
        <div className={styles["admin-catalogue__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-catalogue__header"]}>
        <div className={styles["admin-catalogue__icon-wrapper"]}>
          <Server size={24} strokeWidth={2.5} />
        </div>

        <div className={styles["admin-catalogue__title-group"]}>
          <h2 className={styles["admin-catalogue__title"]}>API Catalogue</h2>
          <p className={styles["admin-catalogue__subtitle"]}>
            Comprehensive documentation for all APIs
          </p>
        </div>

        <div className={styles["admin-catalogue__header-actions"]}>
          {can("api-catalogue", "api-health") && (
            <Button
              text="Health API"
              icon={Activity}
              className={styles["admin-catalogue__btn-secondary"]}
              onClick={() =>
                navigate("/admin/admin-catalogue/health-api", {
                  state: { isAdminView: true },
                })
              }
            />
          )}
          {can("api-catalogue", "add-api") && (
            <Button
              text="Add API"
              icon={Plus}
              onClick={() =>
                navigate("/admin/admin-catalogue/create-api-product")
              }
            />
          )}
        </div>
      </div>

      {/* MAIN CARD */}
      <div className={styles["admin-catalogue__card"]}>
        <div className={styles["admin-catalogue__controls"]}>
          <div className={styles["admin-catalogue__showing-text"]}>
            Showing {apis.length} of {pagination.total} APIs
          </div>

          <div className={styles["admin-catalogue__search-group"]}>
            <Input
              type="search"
              placeholder="Search API Name/Code/Vendor Name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button text="Search" onClick={handleSearch} />
          </div>
        </div>

        {loading ? (
          <TableSkeleton columns={5} rows={6} />
        ) : (
          <APITable
            columns={columns}
            data={apis}
            renderCell={renderCell}
            loading={loading}
          />
        )}

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(limit) => {
            setRowsPerPage(limit);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* MODALS */}
      <ConfirmModal
        open={!!statusModal}
        title="Are you sure you want to change the status?"
        onClose={() => setStatusModal(null)}
        onConfirm={confirmToggle}
      />

      <ConfirmModal
        open={!!failoverModal}
        title="Are you sure you want to change failover status?"
        onClose={() => setFailoverModal(null)}
        onConfirm={confirmFailoverToggle}
      />

      <ConfirmModal
        open={!!downModal}
        title="Are you sure you want to change API Down status?"
        onClose={() => setDownModal(null)}
        onConfirm={confirmDownToggle}
      />
    </div>
  );
};

export default AdminCatalogue;
